import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Download, Image, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

interface SocialCardGeneratorProps {
  initialContent?: {
    title: string;
    excerpt?: string;
    image?: string;
    type: 'blog' | 'travel-byte';
  };
}

const CARD_TEMPLATES = [
  { id: 'instagram-square', label: 'Instagram Square', width: 1080, height: 1080 },
  { id: 'instagram-story', label: 'Instagram Story', width: 1080, height: 1920 },
  { id: 'pinterest', label: 'Pinterest', width: 1000, height: 1500 },
  { id: 'facebook', label: 'Facebook', width: 1200, height: 630 },
  { id: 'twitter', label: 'X/Twitter', width: 1200, height: 675 },
];

const COLOR_THEMES = [
  { id: 'ocean', name: 'Ocean Blue', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'sunset', name: 'Sunset', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'forest', name: 'Forest', gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
  { id: 'midnight', name: 'Midnight', gradient: 'linear-gradient(135deg, #232526 0%, #414345 100%)' },
  { id: 'warm', name: 'Warm Sand', gradient: 'linear-gradient(135deg, #e6b980 0%, #eacda3 100%)' },
  { id: 'tropical', name: 'Tropical', gradient: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)' },
];

export const SocialCardGenerator = ({ initialContent }: SocialCardGeneratorProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [title, setTitle] = useState(initialContent?.title || '');
  const [subtitle, setSubtitle] = useState(initialContent?.excerpt || '');
  const [template, setTemplate] = useState('instagram-square');
  const [colorTheme, setColorTheme] = useState('ocean');
  const [backgroundImage, setBackgroundImage] = useState(initialContent?.image || '');
  const [brandText, setBrandText] = useState('Trotting the World');
  const [isGenerating, setIsGenerating] = useState(false);

  const selectedTemplate = CARD_TEMPLATES.find((t) => t.id === template) || CARD_TEMPLATES[0];
  const selectedTheme = COLOR_THEMES.find((t) => t.id === colorTheme) || COLOR_THEMES[0];

  const generateCard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsGenerating(true);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = selectedTemplate;
    canvas.width = width;
    canvas.height = height;

    try {
      // Draw background
      if (backgroundImage) {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            // Draw image to cover canvas
            const scale = Math.max(width / img.width, height / img.height);
            const x = (width - img.width * scale) / 2;
            const y = (height - img.height * scale) / 2;
            ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
            
            // Add overlay gradient
            const gradient = ctx.createLinearGradient(0, 0, 0, height);
            gradient.addColorStop(0, 'rgba(0,0,0,0.3)');
            gradient.addColorStop(0.5, 'rgba(0,0,0,0.1)');
            gradient.addColorStop(1, 'rgba(0,0,0,0.7)');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, width, height);
            resolve();
          };
          img.onerror = reject;
          img.src = backgroundImage;
        });
      } else {
        // Use gradient background
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        const colors = selectedTheme.gradient.match(/#[a-fA-F0-9]{6}/g) || ['#667eea', '#764ba2'];
        gradient.addColorStop(0, colors[0]);
        gradient.addColorStop(1, colors[1] || colors[0]);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
      }

      // Add decorative elements
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.beginPath();
      ctx.arc(width * 0.9, height * 0.1, width * 0.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(width * 0.1, height * 0.9, width * 0.15, 0, Math.PI * 2);
      ctx.fill();

      // Set up text styling
      const padding = width * 0.08;
      const maxWidth = width - padding * 2;

      // Draw brand text at top
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = `bold ${width * 0.035}px system-ui, sans-serif`;
      ctx.fillText(brandText, padding, padding + width * 0.04);

      // Draw title
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${width * 0.07}px system-ui, sans-serif`;
      const titleLines = wrapText(ctx, title, maxWidth);
      const titleY = height * 0.5;
      titleLines.forEach((line, i) => {
        ctx.fillText(line, padding, titleY + i * width * 0.09);
      });

      // Draw subtitle
      if (subtitle) {
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.font = `${width * 0.04}px system-ui, sans-serif`;
        const subtitleLines = wrapText(ctx, subtitle, maxWidth);
        const subtitleY = titleY + titleLines.length * width * 0.09 + width * 0.05;
        subtitleLines.slice(0, 3).forEach((line, i) => {
          ctx.fillText(line, padding, subtitleY + i * width * 0.055);
        });
      }

      // Draw bottom decoration
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.fillRect(padding, height - padding - 4, width * 0.2, 4);

      toast.success('Card generated!');
    } catch (error) {
      console.error('Error generating card:', error);
      toast.error('Failed to generate card');
    } finally {
      setIsGenerating(false);
    }
  };

  const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const downloadCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `social-card-${template}-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Card downloaded!');
  };

  useEffect(() => {
    if (title) {
      generateCard();
    }
  }, [title, subtitle, template, colorTheme, backgroundImage, brandText]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Social Card Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your headline..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Subtitle (optional)</Label>
            <Textarea
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Add a short description..."
              rows={2}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Template</Label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CARD_TEMPLATES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.label} ({t.width}x{t.height})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Color Theme</Label>
              <Select value={colorTheme} onValueChange={setColorTheme}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLOR_THEMES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ background: t.gradient }}
                        />
                        {t.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Background Image URL (optional)</Label>
            <Input
              value={backgroundImage}
              onChange={(e) => setBackgroundImage(e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="space-y-2">
            <Label>Brand Text</Label>
            <Input
              value={brandText}
              onChange={(e) => setBrandText(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={generateCard} disabled={!title || isGenerating} className="flex-1">
              <Image className="w-4 h-4 mr-2" />
              {isGenerating ? 'Generating...' : 'Generate'}
            </Button>
            <Button variant="outline" onClick={downloadCard} disabled={!title}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center bg-muted/50 rounded-lg p-4 min-h-[400px]">
            <canvas
              ref={canvasRef}
              className="max-w-full max-h-[500px] rounded-lg shadow-lg"
              style={{
                aspectRatio: `${selectedTemplate.width} / ${selectedTemplate.height}`,
              }}
            />
          </div>
          <p className="text-sm text-muted-foreground text-center mt-4">
            {selectedTemplate.label} • {selectedTemplate.width} × {selectedTemplate.height}px
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
